import { useCallback } from 'react'

import { TasksValueObjectsTaskStatusEnum } from 'generated/resources/datatable.graphql'

import TaskFooter from 'modules/inbox_tasks/activities/Task/Footer'
import {
  TaskCustomComponent,
  TaskDefinition,
  TaskFooterComponent,
} from 'modules/inbox_tasks/lib/abstraction'
import { useUpdateTaskStatuses } from 'modules/inbox_tasks/lib/updateTaskStatus/updateTaskStatus'

import BasicTaskContent from './BasicTask'
import { getBasicTaskMetadata } from './BasicTask/metadata'
import { useCompleteStepActionTask } from './completeTask/completeTask'
import { ActionType, StepActionContentComponent, parseConfigurationJson } from './helpers'
import TrainingContent from './Training'
import { getTrainingMetadata } from './Training/metadata'

const ACTION_COMPONENTS: Record<ActionType, StepActionContentComponent> = {
  basic_task: BasicTaskContent,
  training: TrainingContent,
}

const isActionType = (value: string): value is ActionType => value in ACTION_COMPONENTS

const EmployeeLifecycleStepActionContent: TaskCustomComponent<'EmployeeLifecycleStepAction'> = ({
  taskId,
  taskable,
}) => {
  const rawActionType = taskable?.actionType ?? 'basic_task'
  const Component = isActionType(rawActionType)
    ? ACTION_COMPONENTS[rawActionType]
    : ACTION_COMPONENTS.basic_task

  return <Component taskId={taskId} />
}

const EmployeeLifecycleStepActionFooter: TaskFooterComponent<'EmployeeLifecycleStepAction'> = ({
  taskId,
  status,
  taskable,
}) => {
  const updateTaskStatuses = useUpdateTaskStatuses()
  const completeStepActionTask = useCompleteStepActionTask()

  const handleDropdownAction = useCallback(
    async ({ actionId }: { actionId: string }) => {
      const statuses: Record<string, TasksValueObjectsTaskStatusEnum> = {
        'mark-as-done': 'done',
        'mark-as-todo': 'todo',
        'mark-as-in-progress': 'in_progress',
        'mark-as-discarded': 'discarded',
      }

      const newStatus = statuses[actionId]

      if (!newStatus) return

      if (newStatus === 'done' && taskable?.id) {
        await completeStepActionTask({
          stepActionId: taskable.id,
          taskId,
        })
        return true
      }

      await updateTaskStatuses({
        taskIds: [taskId],
        status: newStatus,
      })

      return true
    },
    [completeStepActionTask, taskId, taskable?.id, updateTaskStatuses]
  )

  return (
    <TaskFooter
      taskId={taskId}
      taskKind="manual"
      status={status}
      onClickDropdownAction={handleDropdownAction}
    />
  )
}

export const EmployeeLifecycleStepActionTaskDefinition: TaskDefinition<'EmployeeLifecycleStepAction'> =
  {
    taskableTypename: 'EmployeeLifecycleStepAction',
    initialState: {},
    component: EmployeeLifecycleStepActionContent,
    footer: EmployeeLifecycleStepActionFooter,
    getUrl: ({ info, navigation }) => {
      if (info.actionType !== 'training') return null

      const config = parseConfigurationJson(info.configurationJson)
      const trainingId = config?.training_id as string

      if (!trainingId) return null

      return navigation.trainings.list.details_revamp.view.participants.fullPath({
        id: trainingId,
      })
    },
    getMetadata: ({ info }) => {
      switch (info.actionType) {
        case 'training':
          return getTrainingMetadata()
        case 'basic_task':
        default:
          return getBasicTaskMetadata(info.configurationJson)
      }
    },
  }
